/**
 * QgsSummaryItemPage
 *
 * QGS post-submit summary — mirrors the QAS SummaryItemPage layout so the
 * shell (Unit Information, Audit Details, action buttons) looks familiar,
 * but strips every scoring surface:
 *   - No "Final Score" tile
 *   - No "Passing Threshold"
 *   - No PASS / FAIL badge
 *   - No "Failed Control Items" / "Passed Control Items"
 *
 * In their place, the right column shows the two things the operator
 * cares about at gate submission: Open KOs and Rework Records, sourced
 * from qgsSubmissionStore's latest submission_summary (backend truth,
 * refreshed on every batched flush and again on the final /complete/
 * call). A short list of both is rendered below the tiles for a quick
 * glance without leaving the page.
 */
import React from 'react';
import { ArrowLeft, CheckCircle, FileText, Info, AlertOctagon, Wrench } from 'lucide-react';
import { Card, CardHeader, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import FormDetailsModal from './FormDetailsModal';
import { useTranslation } from '../hooks/useTranslation';
import * as qgsStore from '../qgsSubmissionStore';


function cn(...classes) { return classes.filter(Boolean).join(' '); }


function formatDateTime(v) {
  if (!v) return 'N/A';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return String(v); }
}


function getOperatorName(auditResponse) {
  return auditResponse?.operator_name
    || auditResponse?.operator?.name
    || auditResponse?.created_by_username
    || 'N/A';
}


export default function QgsSummaryItemPage({
  formData,
  selectedForm,
  auditFormData,
  currentAuditResponse,
  onGoBack,
  isReadOnly = false,
}) {
  const { t } = useTranslation();
  const [isFormModalOpen, setIsFormModalOpen] = React.useState(false);
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => qgsStore.subscribe(() => setTick(x => x + 1)), []);

  const auditResponse = currentAuditResponse;
  const form = selectedForm || auditResponse?.audit_form;
  const submittedDate = formatDateTime(auditResponse?.submitted_at || auditResponse?.created_at);

  const summary = qgsStore.getSubmissionSummary();
  const records = React.useMemo(() => qgsStore.allRecords(), [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const koRows = React.useMemo(
    () => Object.entries(records).filter(([, r]) => r.response === 'KO').map(([checkId, r]) => ({ checkId, ...r })),
    [records],
  );
  const reworkRows = React.useMemo(
    () => Object.entries(records).filter(([, r]) => r.response === 'REWORK').map(([checkId, r]) => ({ checkId, ...r })),
    [records],
  );
  const koCount = summary ? summary.knockouts_count : koRows.length;
  const reworkCount = summary ? summary.reworks_count : reworkRows.length;

  const statusLabel = summary?.status || auditResponse?.status || 'SUBMITTED';

  return (
    <main className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <header className="text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{t('summary_item.audit_summary')}</h1>
          <p className="text-gray-600">{t('summary.qgs_subtitle')}</p>
        </header>

        {/* Audit Status Bar — no PASS/FAIL result, no scoring */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-sm text-blue-700">{t('summary_item.status')}:</span>
                  <Badge variant={statusLabel === 'SUBMITTED' ? 'default' : 'secondary'} className="ml-2">
                    {statusLabel}
                  </Badge>
                </div>
                <div className="text-sm">
                  <span className="text-blue-700">{t('summary_item.submitted') || 'Submitted'}:</span>
                  <span className="text-blue-900 font-medium ml-2">{submittedDate}</span>
                </div>
                {summary?.session_id && (
                  <div className="text-sm">
                    <span className="text-blue-700">{t('summary.qgs_session')}:</span>
                    <span className="text-blue-900 font-medium ml-2">{summary.session_id}</span>
                  </div>
                )}
              </div>
              {form && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFormModalOpen(true)}
                  className="bg-white"
                >
                  <Info className="h-4 w-4 mr-2" />
                  {t('summary_item.form_details')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column — Unit Information + Audit Details */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <h3 className="font-semibold text-gray-900">Unit Information</h3>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Serial Number</p>
                    <p className="font-medium">{formData?.serialNumber || auditResponse?.serial_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Unit Model</p>
                    <p className="font-medium">{formData?.unitModel || auditResponse?.unit_model || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Date and Time</p>
                    <p className="font-medium">{submittedDate}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Work Order (OV)</p>
                    <p className="font-medium">{formData?.workOrder || auditResponse?.work_order || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Production Line</p>
                    <p className="font-medium">{formData?.productionLine || auditResponse?.production_line || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">{t('summary.qgs_gate')}</p>
                    <p className="font-medium">{form?.gate_type || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <h3 className="font-semibold text-gray-900">{t('summary_item.audit_details')}</h3>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Operator</p>
                    <p className="font-medium">{getOperatorName(auditResponse)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">{t('summary_item.audit_form')}</p>
                    <p className="font-medium">{form?.form_name || form?.title || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">{t('summary_item.audit_session_id')}</p>
                    <p className="font-medium text-sm">{summary?.session_id || auditResponse?.audit_form_response_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Form ID</p>
                    <p className="font-medium">{form?.id || form?.form_id || auditResponse?.audit_form_id || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column — KO / Rework tiles (no scoring) */}
          <div className="space-y-4">
            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-5 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <AlertOctagon className="w-5 h-5 text-red-600" />
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{t('summary.qgs_open_ko')}</h3>
                </div>
                <div className="text-5xl font-bold text-red-600">{koCount}</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-5 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Wrench className="w-5 h-5 text-orange-600" />
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{t('summary.qgs_rework')}</h3>
                </div>
                <div className="text-5xl font-bold text-orange-600">{reworkCount}</div>
              </CardContent>
            </Card>

            {/* Compact list of the actual rows */}
            {(koRows.length > 0 || reworkRows.length > 0) && (
              <Card>
                <CardContent className="p-3 max-h-72 overflow-y-auto space-y-2">
                  {koRows.map(r => (
                    <div key={`ko-${r.checkId}`} className="flex items-start gap-2 text-xs border-l-2 border-red-500 pl-2 py-1">
                      <span className="font-bold text-red-700 shrink-0">KO</span>
                      <span className="truncate text-gray-800">{r.checkLabel || `Check #${r.checkId}`}</span>
                    </div>
                  ))}
                  {reworkRows.map(r => (
                    <div key={`rw-${r.checkId}`} className="flex items-start gap-2 text-xs border-l-2 border-orange-500 pl-2 py-1">
                      <span className="font-bold text-orange-700 shrink-0">RW</span>
                      <span className="truncate text-gray-800">{r.checkLabel || `Check #${r.checkId}`}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <Button
            variant="outline"
            size="lg"
            onClick={onGoBack}
            className="flex-1 h-12"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {isReadOnly ? t('summary_item.go_back_dashboard') : t('summary_item.go_back_audit')}
          </Button>

          <div className={cn(
            'flex-1 h-12 flex items-center justify-center border-2 rounded-lg px-4',
            statusLabel === 'SUBMITTED' ? 'bg-green-50 border-green-500' : 'bg-slate-50 border-slate-300',
          )}>
            <CheckCircle className={cn('mr-2 h-5 w-5', statusLabel === 'SUBMITTED' ? 'text-green-600' : 'text-slate-500')} />
            <span className={cn(
              'text-lg font-semibold',
              statusLabel === 'SUBMITTED' ? 'text-green-700' : 'text-slate-700',
            )}>
              {statusLabel === 'SUBMITTED' ? t('summary_item.audit_completed') : statusLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Form Details Modal */}
      <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {form?.form_name || form?.title} - {t('summary_item.details')}
            </DialogTitle>
          </DialogHeader>
          <FormDetailsModal
            selectedForm={form}
            auditFormData={auditFormData || { data: auditResponse?.audit_form }}
            isLoadingFormData={false}
            formDataError={null}
          />
        </DialogContent>
      </Dialog>
    </main>
  );
}
